import { useState, useEffect, useRef, useMemo } from 'react';
import {
  useNavigate,
  useSearchParams,
  useParams,
  useLocation,
} from 'react-router-dom';
import toast from 'react-hot-toast';
import { useLiff } from '../../contexts/LiffContext';
import { logErrorToBackend } from '../../utils/errorLogger';
import { getApiBase } from '../../api/apiUtils';
import { getGroupIdFromUrl } from '../../utils/getGroupIdFromUrl';
import { useNavigateWithGroupId } from '../../hooks/useNavigateWithGroupId';
import { buildLiteJoinUrl } from '../../utils/lite-join-url';
import { stripBrandAdFromShareText } from '../../utils/strip-brand-ad';
import { getLiteJwtToken, setLiteJwtToken } from '../../api/liteAuthToken';
import { registerNewUser } from '../../api/contextClient';
import {
  getJoinActivity,
  updateJoinItem,
  getUserItems,
  updateJoinRemarks,
  updateJoinDisplayName,
  type JoinActivity,
  type UserItem,
} from '../../api/liteJoinClient';
import { getActivities, type Activity } from '../../api/liteActivitiesClient';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LogIn, Share2 } from 'lucide-react';

/** 後端 LINE Login state 的 base64url 編碼（redirect_uri + mode: 'choice'） */
function base64UrlEncodeState(state: {
  redirect_uri: string;
  mode: string;
}): string {
  const json = JSON.stringify(state);
  const bytes = new TextEncoder().encode(json);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * 參與購買清單頁面
 * 顯示購買清單，允許使用者點擊加減數量，並顯示統計
 * 使用 shadcn/ui + Vaul 美化界面
 */
/** 從 pathname 解析 /lite/shop/:suffix/join/ 的 suffix（params 未就緒時的 fallback） */
function getSuffixFromPathname(pathname: string): string | null {
  const m = pathname.match(/\/lite\/shop\/([^/]+)\/join\//);
  const s = m?.[1];
  return s && s.length >= 6 && s.length <= 8 ? s : null;
}

export function JoinPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const params = useParams<{ activityId?: string; suffix?: string }>();
  const { profile, liff, isLoggedIn } = useLiff();

  // 取得當前 groupId（LIFF）或 suffix（Square），用於路由跳轉時保留
  const groupId = getGroupIdFromUrl();
  const navigateWithGroupId = useNavigateWithGroupId();
  /** activityId 可來自 query 或 path（/lite/shop/:suffix/join/:activityId） */
  const activityIdFromUrl =
    searchParams.get('activityId') ?? params.activityId ?? null;
  /** suffix：query / params 優先，否則從 pathname 解析（避免首屏 params 未就緒導致漏帶 X-Lite-Square-Suffix） */
  const suffixFromUrl =
    searchParams.get('suffix') ??
    params.suffix ??
    getSuffixFromPathname(location.pathname) ??
    null;

  const [activity, setActivity] = useState<JoinActivity | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [userItems, setUserItems] = useState<UserItem[]>([]); // 已提交的項目（從 API 載入）
  const [pendingItems, setPendingItems] = useState<Record<string, number>>({}); // 待提交的變更：{ productKey: quantity }
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showActivityList, setShowActivityList] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [showStatsDrawer, setShowStatsDrawer] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 是否在編輯模式：false = 收合（總計 + 修改鈕）；true = 展開表單。已有資料時應先收合，按「修改」再展開
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(''); // 顯示名稱（暱稱）；留空時以 LINE 名稱顯示
  const [loadError, setLoadError] = useState<{
    type: 'notFound' | 'noActivities' | null;
  }>({ type: null }); // 載入錯誤類型
  const [remarks, setRemarks] = useState(''); // 參與備註，與 activity.remarks 同步
  /** 送出成功後依商家回覆設定組出的分享文案，供「分享」按鈕與 Modal 使用 */
  const [shareTextAfterSubmit, setShareTextAfterSubmit] = useState<
    string | null
  >(null);
  /** 送出成功後是否顯示分享 Modal */
  const [shareModalOpen, setShareModalOpen] = useState(false);

  /** 長按 +／－：首觸 1s、之後每 500ms 跳 10；計時器、連發 interval、是否已觸發、是否已取消（滑出） */
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressRepeatRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const longPressFiredRef = useRef(false);
  const longPressCancelledRef = useRef(false);
  /** 載入請求序號：僅在「最新一次載入」失敗時才設 loadError，避免並發時先失敗的請求覆蓋已成功的結果 */
  const loadIdRef = useRef(0);

  useEffect(() => {
    if (activity?.remarks !== undefined) setRemarks(activity.remarks || '');
  }, [activity?.id, activity?.remarks]);

  useEffect(() => {
    if (activity?.displayName !== undefined)
      setDisplayName(activity.displayName || '');
  }, [activity?.id, activity?.displayName]);

  // 自動註冊用戶（如果尚未是 lite user）並取得 userId；Square 模式（suffix + token）僅從 token 解析 userId
  useEffect(() => {
    const autoRegisterAndGetUserId = async () => {
      // Square 進入：有 suffix 與 token 時，僅從 JWT 解析 userId，不需 LIFF
      if (suffixFromUrl) {
        const token = getJwtToken();
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            if (payload.user_id) setCurrentUserId(payload.user_id);
          } catch (_) {
            // JWT 解析失敗時略過，不影響後續流程
          }
        }
        return;
      }

      if (!groupId) {
        return;
      }

      try {
        if (liff && isLoggedIn && profile?.userId) {
          const existingToken = getJwtToken();
          if (!existingToken) {
            setIsRegistering(true);
            try {
              const lineIdToken = await liff.getIDToken();
              if (lineIdToken) {
                const registerResult = await registerNewUser({
                  lineIdToken,
                  ...(groupId ? { groupId } : {}),
                });
                console.log(
                  '[JoinPage] ✅ 用戶已註冊/登入:',
                  registerResult.user.id
                );

                setCurrentUserId(registerResult.user.id);
                console.log(
                  '[JoinPage] ✅ 已設置 currentUserId:',
                  registerResult.user.id
                );

                if (registerResult.jwtToken) {
                  localStorage.setItem('jwt_token', registerResult.jwtToken);
                  console.log('[JoinPage] ✅ 已儲存 JWT Token');
                }
              }
            } catch (registerError) {
              console.warn(
                '[JoinPage] 自動註冊用戶失敗（可能已存在）:',
                registerError
              );
            } finally {
              setIsRegistering(false);
            }
          } else {
            // 如果已有 JWT Token，嘗試從 Token 中解析 userId
            try {
              const token = getJwtToken();
              if (token) {
                const payload = JSON.parse(atob(token.split('.')[1]));
                if (payload.user_id) {
                  setCurrentUserId(payload.user_id);
                  console.log(
                    '[JoinPage] ✅ 從 JWT Token 解析出 userId:',
                    payload.user_id
                  );
                } else {
                  console.warn(
                    '[JoinPage] JWT Token 中沒有 user_id，等待註冊完成'
                  );
                }
              }
            } catch (error) {
              console.warn('[JoinPage] 解析 JWT Token 失敗:', error);
            }
          }
        }
      } catch (error) {
        console.error('[JoinPage] 自動註冊失敗:', error);
      }
    };

    autoRegisterAndGetUserId();
  }, [groupId, suffixFromUrl, searchParams, profile, liff, isLoggedIn]);

  // 載入活動資訊或活動列表（從 API）
  useEffect(() => {
    const loadActivityOrList = async () => {
      const activityId = activityIdFromUrl;
      const currentLoadId = ++loadIdRef.current;

      // 重置錯誤狀態
      setLoadError({ type: null });
      setIsLoading(true);

      try {
        const jwtToken = getJwtToken();

        // 有 activityId 但尚無 JWT：不呼叫 API，顯示「請先使用 LINE 登入」（Join 一律走後端 LINE Login，不呼叫 LIFF）
        if (activityId && !jwtToken) {
          setIsLoading(false);
          return;
        }

        if (activityId) {
          // 有 activityId 且有 JWT：載入單個活動
          try {
            const activityData = await getJoinActivity(
              activityId,
              jwtToken || undefined,
              suffixFromUrl ? { squareSuffix: suffixFromUrl } : undefined
            );

            if (!activityData) {
              if (currentLoadId === loadIdRef.current) {
                setLoadError({ type: 'notFound' });
                setActivity(null);
                setShowActivityList(false);
              }
              setIsLoading(false);
              return;
            }
            if (activityData.status !== 'active') {
              if (currentLoadId === loadIdRef.current) {
                setLoadError({ type: 'notFound' });
                setActivity(null);
                setShowActivityList(false);
              }
              setIsLoading(false);
              return;
            }

            setActivity(activityData);
            setShowActivityList(false);
            setLoadError({ type: null });
            // 切換活動時清空待提交狀態，避免殘留導致 hasPendingChanges 並自動展開
            setPendingItems({});

            // 載入用戶商品選擇（非阻塞）
            if (jwtToken && currentUserId) {
              try {
                const joinLiteOpts = suffixFromUrl
                  ? { squareSuffix: suffixFromUrl }
                  : undefined;
                const items = await getUserItems(
                  currentUserId,
                  activityId,
                  jwtToken,
                  joinLiteOpts
                );
                setUserItems(items);
                console.log(
                  '[JoinPage] ✅ 已載入用戶商品選擇:',
                  items.length,
                  '項'
                );

                // 已有資料 → 收合；無資料 → 展開（僅在此處設定，避免 effect 重跑時被覆蓋）
                const hasExistingItems =
                  items.length > 0 && items.some(item => item.quantity > 0);
                setIsEditing(!hasExistingItems);
              } catch (error) {
                console.warn(
                  '[JoinPage] 載入用戶商品選擇失敗（可能尚未選擇）:',
                  error
                );
                setIsEditing(true);
              }
            } else {
              // 尚未有 userId 時仍設為展開，等 effect 重跑並載入 userItems 後再收合
              setIsEditing(true);
            }

            setIsLoading(false);
          } catch (error) {
            console.error('[JoinPage] 載入活動失敗:', error);
            logErrorToBackend(error, {
              context: {
                action: 'loadActivity',
                activityId,
                groupId,
              },
            });
            if (currentLoadId === loadIdRef.current) {
              setLoadError({ type: 'notFound' });
              setActivity(null);
              setShowActivityList(false);
            }
            setIsLoading(false);
          }
        } else {
          // 沒有 activityId：載入活動列表
          if (!jwtToken) {
            toast.error('請先登入');
            setIsLoading(false);
            return;
          }

          try {
            const result = await getActivities(jwtToken);
            const activeActivities = result.activities.filter(
              (act: Activity) => act.status === 'active'
            );

            if (activeActivities.length === 1) {
              // 只有一個活動，直接進入該活動（不顯示列表）
              const singleActivity = activeActivities[0];
              try {
                const activityData = await getJoinActivity(
                  singleActivity.id,
                  jwtToken,
                  suffixFromUrl ? { squareSuffix: suffixFromUrl } : undefined
                );

                if (activityData.status !== 'active') {
                  // 活動狀態不是 active，顯示錯誤
                  setLoadError({ type: 'notFound' });
                  setActivity(null);
                  setShowActivityList(false);
                  setIsLoading(false);
                  return;
                }

                setActivity(activityData);
                setShowActivityList(false);
                setLoadError({ type: null });
                setPendingItems({});

                // 載入用戶商品選擇（非阻塞）
                if (currentUserId) {
                  try {
                    const joinLiteOpts = suffixFromUrl
                      ? { squareSuffix: suffixFromUrl }
                      : undefined;
                    const items = await getUserItems(
                      currentUserId,
                      singleActivity.id,
                      jwtToken,
                      joinLiteOpts
                    );
                    setUserItems(items);
                    const hasExistingItems =
                      items.length > 0 && items.some(item => item.quantity > 0);
                    setIsEditing(!hasExistingItems);
                  } catch (error) {
                    console.warn(
                      '[JoinPage] 載入用戶商品選擇失敗（可能尚未選擇）:',
                      error
                    );
                    setIsEditing(true);
                  }
                } else {
                  setIsEditing(true);
                }

                setIsLoading(false);
              } catch (error) {
                console.error('[JoinPage] 載入單個活動失敗:', error);
                logErrorToBackend(error, {
                  context: {
                    action: 'loadSingleActivity',
                    activityId: singleActivity.id,
                    groupId,
                  },
                });
                if (currentLoadId === loadIdRef.current) {
                  setLoadError({ type: 'notFound' });
                  setActivity(null);
                  setShowActivityList(false);
                }
                setIsLoading(false);
              }
              return;
            }

            // 不只一個活動，顯示活動列表
            setActivities(activeActivities);
            setShowActivityList(true);
            setActivity(null);
            setLoadError(
              activeActivities.length === 0
                ? { type: 'noActivities' }
                : { type: null }
            );
            setIsLoading(false);
          } catch (error) {
            console.error('[JoinPage] 載入活動列表失敗:', error);
            toast.error('載入活動列表失敗');
            setActivities([]);
            setShowActivityList(true);
            setLoadError({ type: 'noActivities' });
            setIsLoading(false);
          }
        }
      } catch (error) {
        console.error('[JoinPage] 載入失敗:', error);
        toast.error('載入失敗');
        setIsLoading(false);
      }
    };

    if (!isRegistering) {
      const jwtToken = getJwtToken();
      if (!jwtToken && liff && isLoggedIn && profile?.userId) {
        let checkInterval: NodeJS.Timeout | null = null;
        let timeoutId: NodeJS.Timeout | null = null;

        checkInterval = setInterval(() => {
          const token = getJwtToken();
          if (token) {
            if (checkInterval) clearInterval(checkInterval);
            if (timeoutId) clearTimeout(timeoutId);
            loadActivityOrList();
          }
        }, 500);

        timeoutId = setTimeout(() => {
          if (checkInterval) clearInterval(checkInterval);
          loadActivityOrList();
        }, 5000);

        return () => {
          if (checkInterval) clearInterval(checkInterval);
          if (timeoutId) clearTimeout(timeoutId);
        };
      }

      loadActivityOrList();
    }
  }, [
    activityIdFromUrl,
    suffixFromUrl,
    searchParams,
    currentUserId,
    groupId,
    isRegistering,
    liff,
    isLoggedIn,
    profile,
  ]);

  /**
   * 取得當前用戶對特定商品的數量（包含待提交的變更）
   */
  const getItemQuantity = (productKey: string): number => {
    if (productKey in pendingItems) return pendingItems[productKey];
    const savedItem = userItems.find(item => item.productKey === productKey);
    return savedItem?.quantity || 0;
  };

  /** 扁平化可選清單：單一商品一列，多規格每規格一列（productKey = productId 或 productId:specId） */
  const selectableRows = useMemo(() => {
    if (!activity?.productList?.length) return [];
    const rows: Array<{
      productKey: string;
      name: string;
      price: number;
      productId: string;
      limit: number;
      totalQuantity: number;
    }> = [];
    for (const p of activity.productList) {
      const limit =
        'maxTotalQuantity' in p && p.specs?.length
          ? (p.maxTotalQuantity ?? 0)
          : 'quantity' in p
            ? (p.quantity ?? 0)
            : 0;
      const totalQuantity = p.totalQuantity ?? 0;
      if ('specs' in p && p.specs?.length) {
        for (const s of p.specs) {
          rows.push({
            productKey: `${p.id}:${s.id}`,
            name: `${p.name} ${s.name}`.trim(),
            price: s.price,
            productId: p.id,
            limit,
            totalQuantity,
          });
        }
      } else {
        rows.push({
          productKey: p.id,
          name: p.name,
          price: 'price' in p ? p.price : 0,
          productId: p.id,
          limit,
          totalQuantity,
        });
      }
    }
    return rows;
  }, [activity?.productList]);

  /** 當前用戶在該 product（含多規格）的數量加總 */
  const getProductTotalQuantityByUser = (productId: string): number =>
    selectableRows
      .filter(r => r.productId === productId)
      .reduce((sum, r) => sum + getItemQuantity(r.productKey), 0);

  /**
   * 增加商品數量（只更新本地狀態，不調用 API）
   * 以 setState(prev=>) 讀取最新數量，避免 setTimeout/setInterval 閉包拿到舊的 pendingItems。
   * @param product 可選，長按 +10 時用於依上限 capped
   * @param ourSaved 可選，該商品已提交的數量（用於計算剩餘可選；未傳則不 cap）
   * @returns 是否成功增加（如果達到上限則返回 false）
   */
  const handleIncreaseBy = (
    productKey: string,
    delta: number,
    product?: { quantity?: number; totalQuantity?: number },
    ourSaved?: number
  ): boolean => {
    let canIncrease = true;
    setPendingItems(prev => {
      const currentQty =
        productKey in prev
          ? prev[productKey]
          : (userItems.find(i => i.productKey === productKey)?.quantity ?? 0);

      // 檢查是否有數量上限
      if (product && (product.quantity ?? 0) > 0) {
        const limit = product.quantity ?? 0;
        const totalQty = product.totalQuantity ?? 0;
        const savedQty =
          ourSaved !== undefined
            ? ourSaved
            : (userItems.find(i => i.productKey === productKey)?.quantity ?? 0);

        // 計算剩餘可選數量：上限 - 總已選數量 + 我們已選的數量
        // 這樣可以計算出我們還能再選多少
        const remaining = limit - totalQty + savedQty;

        if (remaining <= 0) {
          canIncrease = false;
          return prev; // 不更新狀態
        }

        // 計算新數量，確保不超過上限
        let newQty = currentQty + delta;
        // 最大可選數量 = 我們已選的數量 + 剩餘可選數量
        const maxQty = savedQty + remaining;
        if (newQty > maxQty) {
          newQty = maxQty;
        }

        // 如果新數量等於當前數量，表示無法增加
        if (newQty === currentQty) {
          canIncrease = false;
          return prev;
        }

        return { ...prev, [productKey]: newQty };
      }

      // 沒有數量上限，直接增加
      const newQty = currentQty + delta;
      return { ...prev, [productKey]: newQty };
    });

    return canIncrease;
  };

  /**
   * 減少商品數量（只更新本地狀態，不調用 API）
   * 以 setState(prev=>) 讀取最新數量，避免 setTimeout/setInterval 閉包拿到舊的 pendingItems。
   */
  const handleDecreaseBy = (productKey: string, delta: number) => {
    setPendingItems(prev => {
      const currentQty =
        productKey in prev
          ? prev[productKey]
          : (userItems.find(i => i.productKey === productKey)?.quantity ?? 0);
      if (currentQty <= 0) return prev;
      const newQty = Math.max(0, currentQty - delta);
      return { ...prev, [productKey]: newQty };
    });
  };

  /** 長按：首觸 500ms，之後每 500ms 連發 ±10 */
  const LONG_PRESS_MS = 500;
  const LONG_PRESS_REPEAT_MS = 500;

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    if (longPressRepeatRef.current) {
      clearInterval(longPressRepeatRef.current);
      longPressRepeatRef.current = null;
    }
  };

  /** 卸載時清除長按計時/連發，避免漏清 */
  useEffect(() => () => clearLongPressTimer(), []);

  /**
   * 取得 JWT Token（從 URL 或 storage）；若從 URL 讀到則寫入 storage 並清除網址列上的 token 參數（避免外洩）
   */
  const getJwtToken = (): string | null => {
    if (typeof window === 'undefined') return null;

    const urlParams = new URLSearchParams(window.location.search);
    const fromUrl = urlParams.get('jwt_token') || urlParams.get('token');
    if (fromUrl) {
      setLiteJwtToken(fromUrl);
      // 從 URL 移除 token / jwt_token，避免留在 history 或遭分享外洩
      urlParams.delete('token');
      urlParams.delete('jwt_token');
      const newSearch = urlParams.toString();
      const newUrl =
        window.location.pathname +
        (newSearch ? `?${newSearch}` : '') +
        window.location.hash;
      window.history.replaceState(null, '', newUrl);
      return fromUrl;
    }

    return getLiteJwtToken();
  };

  /**
   * 開啟系統分享選單（手機上可選擇 LINE、複製、其他 App 等）
   * 優先使用 navigator.share，無則改為複製到剪貼簿
   */
  const shareToSystemSheet = async (text: string): Promise<void> => {
    try {
      if (
        typeof navigator !== 'undefined' &&
        typeof navigator.share === 'function'
      ) {
        await navigator.share({ text });
        toast.success('已分享', { duration: 2000 });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success('已複製至剪貼簿，可貼到聊天分享', { duration: 2500 });
      }
    } catch (err) {
      if (
        err instanceof Error &&
        !err.message?.includes('cancel') &&
        !err.message?.toLowerCase().includes('abort')
      ) {
        try {
          await navigator.clipboard.writeText(text);
          toast.success('已複製至剪貼簿，可貼到聊天分享', { duration: 2500 });
        } catch {
          // 忽略
        }
      }
    }
  };

  /**
   * 分享到 LINE：LIFF 內用 shareTargetPicker；一般瀏覽器用 line.me/R/share 開啟 LINE 分享
   */
  const shareToLine = (text: string): void => {
    const shareTargetPicker =
      liff &&
      'shareTargetPicker' in liff &&
      typeof (
        liff as unknown as {
          shareTargetPicker?: (m: unknown[]) => Promise<unknown>;
        }
      ).shareTargetPicker === 'function'
        ? (
            liff as unknown as {
              shareTargetPicker: (m: unknown[]) => Promise<unknown>;
            }
          ).shareTargetPicker
        : null;
    if (shareTargetPicker) {
      shareTargetPicker([{ type: 'text', text }])
        .then(result => {
          if (result !== undefined && result !== null) {
            toast.success('已分享至 LINE', { duration: 2000 });
          }
        })
        .catch(() => {
          openLineShareUrl(text);
        });
    } else {
      openLineShareUrl(text);
    }
  };

  /**
   * 使用 LINE R/share URL 開啟 LINE 分享（非 LIFF 或 shareTargetPicker 不可用時）
   */
  const openLineShareUrl = (text: string): void => {
    const lineShareUrl = `https://line.me/R/share?text=${encodeURIComponent(text)}`;
    window.open(lineShareUrl, '_blank', 'noopener,noreferrer');
    toast.success('已開啟 LINE 分享', { duration: 2000 });
  };

  /**
   * 依商家回覆設定組出的文案，執行分享：優先系統分享選單（讓用戶選擇），其次複製
   * 供頁面上「分享」按鈕使用
   */
  const shareToLineOrClipboard = async (text: string): Promise<void> => {
    await shareToSystemSheet(text);
  };

  /**
   * 批次提交所有變更
   */
  const handleSubmit = async () => {
    // 優先從 URL 取得；若從「僅有一個活動」進入則 URL 可能無 activityId，改從已載入的 activity 取得
    const activityId = activityIdFromUrl ?? activity?.id ?? null;
    if (!activityId) {
      toast.error('活動 ID 不存在');
      return;
    }

    if (!currentUserId) {
      toast.error('請先登入');
      const jwtToken = getJwtToken();
      if (jwtToken && liff && isLoggedIn && profile?.userId) {
        try {
          const lineIdToken = await liff.getIDToken();
          if (lineIdToken) {
            const registerResult = await registerNewUser({
              lineIdToken,
              ...(groupId ? { groupId } : {}),
            });
            setCurrentUserId(registerResult.user.id);
            // 遞迴調用
            handleSubmit();
            return;
          }
        } catch (error) {
          console.error('[JoinPage] 重新註冊失敗:', error);
          toast.error('請先登入');
          return;
        }
      }
      return;
    }

    // 計算每個商品需要 add/remove 的次數
    const changes: Array<{
      productKey: string;
      targetQty: number;
      savedQty: number;
    }> = [];

    Object.entries(pendingItems).forEach(([productKey, pendingQty]) => {
      const savedItem = userItems.find(item => item.productKey === productKey);
      const savedQty = savedItem?.quantity || 0;

      // 只處理有變更的商品
      if (pendingQty !== savedQty) {
        changes.push({
          productKey,
          targetQty: pendingQty,
          savedQty,
        });
      }
    });

    const remarksChanged = remarks !== (activity?.remarks ?? '');
    const hasDisplayNameChange =
      (displayName ?? '').trim() !== (activity?.displayName ?? '').trim();
    if (changes.length === 0 && !remarksChanged && !hasDisplayNameChange) {
      toast.success('沒有變更需要提交');
      return;
    }

    setIsSubmitting(true);
    const jwtToken = getJwtToken();

    console.log('[JoinPage] 🔥 開始提交:', {
      activityId,
      currentUserId,
      hasJwtToken: !!jwtToken,
      changesCount: changes.length,
      changes,
    });

    try {
      if (!jwtToken) {
        throw new Error('缺少 JWT Token，請先登入');
      }

      if (!currentUserId) {
        throw new Error('缺少用戶 ID');
      }

      const joinLiteOpts = suffixFromUrl
        ? { squareSuffix: suffixFromUrl }
        : undefined;

      if (remarksChanged) {
        await updateJoinRemarks(activityId, remarks, jwtToken, joinLiteOpts);
        setActivity(prev => (prev ? { ...prev, remarks } : prev));
      }

      if (hasDisplayNameChange) {
        const trimmedName = displayName.trim() || null;
        await updateJoinDisplayName(
          activityId,
          trimmedName,
          jwtToken,
          joinLiteOpts
        );
        // 重新取得活動（含 displayName），確保「修改」表單與下次進入都能顯示已存暱稱
        try {
          const fresh = await getJoinActivity(
            activityId,
            jwtToken,
            joinLiteOpts
          );
          setActivity(fresh);
        } catch {
          setActivity(prev =>
            prev ? { ...prev, displayName: trimmedName } : prev
          );
        }
      }

      if (changes.length > 0) {
        // 批次調用 API
        const promises = changes.map(
          async ({ productKey, targetQty, savedQty }) => {
            const diff = targetQty - savedQty;

            console.log('[JoinPage] 處理商品變更:', {
              productKey,
              targetQty,
              savedQty,
              diff,
            });

            // 計算需要 add 或 remove 的次數
            const actions: Array<'add' | 'remove'> = [];
            if (diff > 0) {
              for (let i = 0; i < diff; i++) {
                actions.push('add');
              }
            } else if (diff < 0) {
              for (let i = 0; i < Math.abs(diff); i++) {
                actions.push('remove');
              }
            }

            // 依序執行每個 action
            let lastItem: UserItem | null = null;
            for (const action of actions) {
              console.log('[JoinPage] 執行 action:', {
                productKey,
                action,
                activityId,
                currentUserId,
              });
              try {
                const joinLiteOpts = suffixFromUrl
                  ? { squareSuffix: suffixFromUrl }
                  : undefined;
                const result = await updateJoinItem(
                  activityId,
                  currentUserId,
                  productKey,
                  action,
                  jwtToken,
                  profile?.userId ?? undefined,
                  joinLiteOpts
                );
                lastItem = result.data;
                console.log('[JoinPage] ✅ Action 成功:', {
                  productKey,
                  action,
                  itemId: lastItem?.id,
                });
                // 補正發生時僅記錄，不再透過 liff.sendMessages 觸發 webhook（LIFF messaging 已改為由用戶主動分享）
              } catch (actionError) {
                console.error('[JoinPage] ❌ Action 失敗:', {
                  productKey,
                  action,
                  error: actionError,
                });
                throw actionError; // 重新拋出錯誤，讓 Promise.all 捕獲
              }
            }

            return lastItem;
          }
        );

        console.log('[JoinPage] 等待所有 promises 完成...');
        const results = await Promise.all(promises);
        console.log('[JoinPage] ✅ 所有 promises 完成:', {
          resultsCount: results.length,
        });

        // 後端若以 lineUserId find-or-create，會回傳 effective userId；用於重新載入與同步 state
        const effectiveUserId =
          (results[0] as UserItem | null)?.userId ?? currentUserId;
        if (effectiveUserId !== currentUserId) {
          setCurrentUserId(effectiveUserId);
        }

        // 清空待提交的變更、退出編輯模式後，先立即顯示分享 Modal，再於背景同步商品選擇
        setPendingItems({});
        // 背景重新載入用戶商品選擇（不阻塞分享 Modal 顯示）
        const joinLiteOpts = suffixFromUrl
          ? { squareSuffix: suffixFromUrl }
          : undefined;
        void getUserItems(effectiveUserId, activityId, jwtToken, joinLiteOpts)
          .then(updatedItems => {
            setUserItems(updatedItems);
          })
          .catch(reloadError => {
            console.warn(
              '[JoinPage] 重新載入商品選擇失敗（不影響已提交）:',
              reloadError
            );
          });
      }

      // 退出編輯模式（已有記錄會在 userItems 更新後自動顯示修改頁面）
      setIsEditing(false);

      // 組出「用戶自訂回覆格式」文案，供送出後分享（Group / Square 共用邏輯）
      // 無設定時用預設（與設定頁「自訂一開始就打開」一致），確保會帶活動連結
      const fmt = activity?.userReplyFormat ?? {
        includeActivityUrl: true,
        prefix: '',
        suffix: '',
      };
      let shareText: string | null = null;
      if (
        activity &&
        (changes.length > 0 || remarksChanged || hasDisplayNameChange)
      ) {
        const threshold = fmt?.remainingBelowThreshold ?? null;
        const lines: string[] = [];

        // 商品數量變更摘要（顯示操作後的結果，例如「土司：3」）
        changes.forEach(({ productKey, targetQty, savedQty }) => {
          const row = selectableRows.find(r => r.productKey === productKey);
          const name = row?.name ?? productKey;
          let line = `${name}：${targetQty}`;
          const limit = row?.limit ?? 0;
          if (limit > 0 && threshold != null) {
            const d = targetQty - savedQty;
            const remaining = Math.max(
              0,
              limit - (row?.totalQuantity ?? 0) - d
            );
            if (remaining < threshold) {
              line += ` (數量只剩 ${remaining} 個)`;
            }
          }
          lines.push(line);
        });

        // 備註內容：若有變更就直接印出文字
        if (remarksChanged) {
          const trimmedRemarks = remarks.trim();
          if (trimmedRemarks) {
            lines.push(`備註：${trimmedRemarks}`);
          } else if (changes.length === 0 && !hasDisplayNameChange) {
            // 僅備註從有內容改成空白時，保留一行提示
            lines.push('備註已儲存');
          }
        }

        // 暱稱變更：印出「暱稱已更新為 XX」
        if (hasDisplayNameChange) {
          const trimmedName = displayName.trim();
          if (trimmedName) {
            lines.push(`暱稱已更新為：${trimmedName}`);
          }
        }

        const main = lines.length > 0 ? lines.join('\n') : '已儲存';
        const pre = fmt?.prefix?.trim();
        const suf = fmt?.suffix?.trim();
        const effectiveActivityId = activityId ?? activity?.id;
        const joinUrl =
          fmt?.includeActivityUrl && effectiveActivityId
            ? buildLiteJoinUrl({
                shortUrl: activity?.shortUrl,
                origin:
                  typeof window !== 'undefined' ? window.location.origin : '',
                groupId: groupId ?? undefined,
                suffix: suffixFromUrl ?? undefined,
                activityId: effectiveActivityId,
              })
            : null;
        // URL 放在 CTA（主文/後綴）下方，並加縮排，避免與連結預覽（og:title/description）重複
        if (joinUrl) {
          const body = [pre, main, suf].filter(Boolean).join('\n') || main;
          shareText = body ? `${body}\n  ${joinUrl}` : `  ${joinUrl}`;
        } else {
          shareText = [pre, main, suf].filter(Boolean).join('\n') || main;
        }
      }

      // Toast 訊息：依不同變更類型顯示對應文案
      if (changes.length === 0 && remarksChanged && !hasDisplayNameChange) {
        const trimmedRemarks = remarks.trim();
        toast.success(
          trimmedRemarks ? `備註已儲存：${trimmedRemarks}` : '備註已儲存'
        );
      } else if (
        changes.length === 0 &&
        hasDisplayNameChange &&
        !remarksChanged
      ) {
        const trimmedName = displayName.trim();
        toast.success(
          trimmedName ? `暱稱已更新為：${trimmedName}` : '暱稱已更新'
        );
      } else if (
        changes.length === 0 &&
        remarksChanged &&
        hasDisplayNameChange
      ) {
        const trimmedRemarks = remarks.trim();
        const trimmedName = displayName.trim();
        const parts: string[] = [];
        if (trimmedRemarks) parts.push(`備註：${trimmedRemarks}`);
        if (trimmedName) parts.push(`暱稱：${trimmedName}`);
        toast.success(parts.length > 0 ? parts.join('\n') : '已儲存');
      } else {
        toast.success('已成功提交您的選擇');
      }

      // 送出成功後立即顯示分享 Modal（不再等待 getUserItems）；過濾品牌廣告行，確保 Join 分享不出現廣告文
      const rawShare = shareText?.trim() ?? '';
      const shareTextForModal = stripBrandAdFromShareText(rawShare) || '已送出';
      setShareTextAfterSubmit(shareTextForModal);
      setShareModalOpen(true);
    } catch (error: unknown) {
      console.error('[JoinPage] ❌ 提交失敗:', error);
      const err = error instanceof Error ? error : new Error(String(error));
      console.error('[JoinPage] 錯誤詳情:', {
        message: err.message,
        stack: err.stack,
        error,
      });
      let errorMessage = err.message || '提交失敗，請稍後再試';

      // 檢查是否為數量上限錯誤
      if (
        errorMessage.includes('數量上限') ||
        errorMessage.includes('已達數量上限') ||
        errorMessage.includes('無法再增加')
      ) {
        errorMessage = '該商品已達數量上限，無法再增加';
      }

      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 計算統計（依扁平化可選清單，含多規格）
  const myTotalQuantity = selectableRows.reduce(
    (sum, row) => sum + getItemQuantity(row.productKey),
    0
  );
  const myTotalAmount = selectableRows.reduce(
    (sum, row) => sum + getItemQuantity(row.productKey) * row.price,
    0
  );

  // 檢查是否有未提交的變更
  const hasPendingChanges =
    Object.keys(pendingItems).length > 0 &&
    Object.entries(pendingItems).some(([productKey, pendingQty]) => {
      const savedItem = userItems.find(item => item.productKey === productKey);
      const savedQty = savedItem?.quantity || 0;
      return pendingQty !== savedQty;
    });

  // 使用者再次編輯時隱藏「分享」按鈕，避免沿用舊的分享文案
  useEffect(() => {
    if (hasPendingChanges) setShareTextAfterSubmit(null);
  }, [hasPendingChanges]);

  // 檢查是否有已存在的記錄（從 userItems，不包含 pendingItems）
  const hasExistingItems =
    userItems.length > 0 && userItems.some(item => item.quantity > 0);

  // 當有待提交變更且目前收合時，自動展開（僅在用戶點過「修改」後才可能 hasPendingChanges，因載入時已清空 pendingItems）
  useEffect(() => {
    if (hasPendingChanges && !isEditing) setIsEditing(true);
  }, [hasPendingChanges, isEditing]);

  /** 是否有「全部」數量統計可顯示（來自 activity.productList 的 totalQuantity，為所有參與者加總） */
  const hasAllParticipantsStats =
    activity?.productList?.some(p => (p.totalQuantity ?? 0) > 0) ?? false;

  // 活動列表頁面
  if (showActivityList) {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
        <div className="max-w-2xl mx-auto space-y-4">
          <div className="text-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              參與活動
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              選擇要參與的活動
            </p>
          </div>

          {activities.length === 0 || loadError.type === 'noActivities' ? (
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-5xl mb-4">📋</div>
                <CardTitle className="text-xl mb-2">目前沒有活動</CardTitle>
                <CardDescription className="mb-6">
                  目前沒有正在進行的活動
                </CardDescription>
                <Button onClick={() => navigateWithGroupId('/lite')}>
                  返回主頁
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {activities.map(act => (
                <Card
                  key={act.id}
                  className="cursor-pointer hover:shadow-lg transition-shadow active:scale-[0.98]"
                  onClick={() => {
                    if (groupId) {
                      navigate(
                        `/lite/join?groupId=${encodeURIComponent(groupId)}&activityId=${encodeURIComponent(act.id)}`
                      );
                    } else {
                      navigate(
                        `/lite/join?activityId=${encodeURIComponent(act.id)}`
                      );
                    }
                  }}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-xl">{act.title}</CardTitle>
                        {act.description && (
                          <CardDescription className="mt-2">
                            {act.description}
                          </CardDescription>
                        )}
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
                          {act.startDate && (
                            <span>
                              開始：
                              {new Date(act.startDate).toLocaleDateString(
                                'zh-TW'
                              )}
                            </span>
                          )}
                          {act.endDate && (
                            <span>
                              結束：
                              {new Date(act.endDate).toLocaleDateString(
                                'zh-TW'
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                        進行中
                      </span>
                    </div>
                  </CardHeader>
                  <CardFooter>
                    <Button
                      className="w-full"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (groupId) {
                          navigate(
                            `/lite/join?groupId=${encodeURIComponent(groupId)}&activityId=${encodeURIComponent(act.id)}`
                          );
                        } else {
                          navigate(
                            `/lite/join?activityId=${encodeURIComponent(act.id)}`
                          );
                        }
                      }}
                    >
                      立即參與
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // 有活動 ID 但尚無 JWT → 須先透過後端 LINE Login 登入（Join 不呼叫 LIFF）
  const hasJwt =
    getLiteJwtToken() ||
    (typeof window !== 'undefined' &&
      (new URLSearchParams(window.location.search).get('token') ||
        new URLSearchParams(window.location.search).get('jwt_token')));
  if (
    activityIdFromUrl &&
    !hasJwt &&
    !isLoading &&
    !activity &&
    !showActivityList
  ) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="text-5xl mb-4">🔐</div>
            <CardTitle className="text-2xl mb-2">請先使用 LINE 登入</CardTitle>
            <CardDescription className="mb-6">
              登入後即可查看並參加此活動
            </CardDescription>
            <Button
              onClick={() => {
                const redirectUri = window.location.href.split('#')[0];
                const state = base64UrlEncodeState({
                  redirect_uri: redirectUri,
                  mode: 'choice',
                });
                const apiBase = getApiBase();
                const url = `${apiBase}/api/lite/auth/line?redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(state)}`;
                window.location.href = url;
              }}
              className="gap-2"
            >
              <LogIn className="h-4 w-4" />
              使用 LINE 登入
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // 沒有活動（有 activityId 但載入失敗或活動不存在）
  // 只有在載入完成且確實有錯誤時才顯示
  if (
    !isLoading &&
    !activity &&
    !showActivityList &&
    loadError.type === 'notFound'
  ) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="text-5xl mb-4">📋</div>
            <CardTitle className="text-2xl mb-2">找不到活動</CardTitle>
            <CardDescription className="mb-6">
              請確認連結是否正確
            </CardDescription>
            <Button
              onClick={() => {
                if (groupId) {
                  navigate(`/lite?groupId=${encodeURIComponent(groupId)}`);
                } else {
                  navigate('/lite');
                }
              }}
            >
              返回
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // 如果還在載入中，顯示載入狀態
  if (isLoading) {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  // 如果沒有活動且不在載入中，也不顯示錯誤，可能是其他狀態，顯示載入中
  if (!activity && !showActivityList && !isLoading) {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  // 確保 activity 存在才渲染活動頁面
  if (!activity) {
    return null;
  }

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* 標題 */}
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            {activity.title}
          </h1>
          {activity.description && (
            <p className="text-sm sm:text-base text-gray-600 mb-2">
              {activity.description}
            </p>
          )}
        </div>

        {/* 全部統計按鈕：顯示所有參與者的數量加總（非僅自己） */}
        {hasAllParticipantsStats && (
          <>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                // 先立即打開 Drawer，避免等待 API 導致體感延遲
                setShowStatsDrawer(true);

                const token = getJwtToken();
                if (token && activity) {
                  (async () => {
                    try {
                      const fresh = await getJoinActivity(
                        activity.id,
                        token,
                        suffixFromUrl
                          ? { squareSuffix: suffixFromUrl }
                          : undefined
                      );
                      setActivity(fresh);
                    } catch {
                      // 略過錯誤，維持既有 activity 數據
                    }
                  })();
                }
              }}
            >
              查看全部統計
            </Button>
            <Drawer open={showStatsDrawer} onOpenChange={setShowStatsDrawer}>
              <DrawerContent>
                <DrawerHeader>
                  <DrawerTitle>全部統計</DrawerTitle>
                  <DrawerDescription>所有參與者的數量加總</DrawerDescription>
                </DrawerHeader>
                <div className="px-4 pb-4 space-y-3">
                  {activity.productList.map(product => {
                    const totalQty = Number(product.totalQuantity ?? 0);
                    const limit =
                      'maxTotalQuantity' in product && product.specs?.length
                        ? Number(product.maxTotalQuantity ?? 0)
                        : Number(
                            'quantity' in product ? (product.quantity ?? 0) : 0
                          );
                    const atLimit = limit > 0 && totalQty >= limit;
                    return (
                      <div
                        key={product.id}
                        className="flex justify-between items-center text-sm py-2 border-b border-gray-200"
                      >
                        <span className="text-muted-foreground">
                          {product.name}
                          {atLimit && (
                            <span className="text-amber-600 ml-1">
                              （已達上限）
                            </span>
                          )}
                        </span>
                        <span className="font-medium tabular-nums shrink-0 ml-2">
                          {limit > 0
                            ? `${totalQty} / ${limit}`
                            : String(totalQty)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </DrawerContent>
            </Drawer>
          </>
        )}

        {/* 已有記錄且沒有待提交的變更且不在編輯模式：顯示總計和修改鈕（修改頁面） */}
        {hasExistingItems && !hasPendingChanges && !isEditing ? (
          <>
            {/* 總計 */}
            {myTotalQuantity > 0 && (
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6 space-y-3">
                  {/* 列出選擇的商品（含多規格） */}
                  {selectableRows.map(row => {
                    const qty = getItemQuantity(row.productKey);
                    if (qty <= 0) return null;
                    return (
                      <div
                        key={row.productKey}
                        className="flex justify-between items-center text-sm"
                      >
                        <span className="text-blue-700">{row.name}</span>
                        <span className="text-blue-900 font-medium">{qty}</span>
                      </div>
                    );
                  })}
                  {remarks.trim() && (
                    <div className="pt-2 border-t border-blue-300">
                      <div className="text-xs text-blue-600 mb-1">備註</div>
                      <div className="text-sm text-blue-900">{remarks}</div>
                    </div>
                  )}
                  <div className="flex justify-between items-center text-lg font-bold pt-2 border-t border-blue-300">
                    <span className="text-blue-900">總計：</span>
                    <span className="text-blue-900">NT${myTotalAmount}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 修改按鈕 */}
            <Button
              className="w-full"
              size="lg"
              variant="outline"
              onClick={() => {
                console.log('[JoinPage] 點擊修改按鈕，切換到編輯模式');
                setIsEditing(true);
                // 將 userItems 同步到 pendingItems，讓用戶可以看到當前選擇
                const initialPendingItems: Record<string, number> = {};
                userItems.forEach(item => {
                  if (item.quantity > 0) {
                    initialPendingItems[item.productKey] = item.quantity;
                  }
                });
                setPendingItems(initialPendingItems);
              }}
            >
              修改
            </Button>
          </>
        ) : (
          <>
            {/* 暱稱（顯示名稱） */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">暱稱</CardTitle>
                <CardDescription>留空則以 LINE 名稱顯示給主辦</CardDescription>
              </CardHeader>
              <CardContent>
                <input
                  type="text"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  placeholder="留空以 LINE 名稱顯示"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  maxLength={50}
                />
              </CardContent>
            </Card>

            {/* 商品列表 */}
            <Card>
              <CardHeader>
                <CardTitle>商品列表</CardTitle>
                <CardDescription>長按加減按鈕以10跳動</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectableRows.map(row => {
                  const selectedQty = getItemQuantity(row.productKey);
                  const productTotalByUser = getProductTotalQuantityByUser(
                    row.productId
                  );
                  const atLimit =
                    row.limit > 0 && row.totalQuantity >= row.limit;

                  return (
                    <Card key={row.productKey} className="bg-gray-50">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h3 className="font-semibold text-lg text-gray-900">
                                {row.name}
                              </h3>
                              <span className="text-sm text-muted-foreground">
                                NT${row.price}
                              </span>
                            </div>
                            {row.limit > 0 && (
                              <p className="text-xs text-muted-foreground mt-1">
                                已選 {productTotalByUser} / 總量 {row.limit}
                                {atLimit && (
                                  <span className="text-amber-600 font-medium ml-1">
                                    已達上限
                                  </span>
                                )}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* 數量控制（select-none、touch-callout 避免長按觸發選取/系統選單） */}
                        <div
                          className="flex items-center justify-between select-none"
                          style={
                            {
                              WebkitTouchCallout: 'none',
                            } as React.CSSProperties
                          }
                        >
                          <div className="flex items-center gap-3">
                            <Button
                              variant="outline"
                              size="icon"
                              disabled={selectedQty <= 0}
                              className="w-12 h-12 text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-50 select-none"
                              style={
                                {
                                  WebkitTouchCallout: 'none',
                                } as React.CSSProperties
                              }
                              onContextMenu={e => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onPointerDown={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (selectedQty <= 0) return;
                                longPressFiredRef.current = false;
                                longPressCancelledRef.current = false;
                                clearLongPressTimer();
                                longPressTimerRef.current = setTimeout(() => {
                                  longPressTimerRef.current = null;
                                  longPressFiredRef.current = true;
                                  handleDecreaseBy(row.productKey, 10);
                                  longPressRepeatRef.current = setInterval(
                                    () => {
                                      handleDecreaseBy(row.productKey, 10);
                                    },
                                    LONG_PRESS_REPEAT_MS
                                  );
                                }, LONG_PRESS_MS);
                              }}
                              onPointerUp={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                clearLongPressTimer();
                                if (
                                  !longPressFiredRef.current &&
                                  !longPressCancelledRef.current
                                ) {
                                  handleDecreaseBy(row.productKey, 1);
                                }
                                longPressFiredRef.current = false;
                                longPressCancelledRef.current = false;
                              }}
                              onPointerLeave={() => {
                                clearLongPressTimer();
                                longPressCancelledRef.current = true;
                              }}
                              onPointerCancel={() => {
                                clearLongPressTimer();
                                longPressCancelledRef.current = true;
                              }}
                            >
                              −
                            </Button>
                            <span className="text-xl font-semibold text-gray-900 min-w-[3rem] text-center select-none">
                              {selectedQty}
                            </span>
                            <Button
                              variant="outline"
                              size="icon"
                              disabled={
                                row.limit > 0 && row.totalQuantity >= row.limit
                              }
                              className="w-12 h-12 text-green-600 border-green-200 hover:bg-green-50 disabled:opacity-50 select-none"
                              style={
                                {
                                  WebkitTouchCallout: 'none',
                                } as React.CSSProperties
                              }
                              onContextMenu={e => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onPointerDown={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (
                                  row.limit > 0 &&
                                  row.totalQuantity >= row.limit
                                ) {
                                  toast.error('該商品已達數量上限，無法再增加');
                                  return;
                                }
                                longPressFiredRef.current = false;
                                longPressCancelledRef.current = false;
                                clearLongPressTimer();
                                const ourSaved =
                                  row.limit > 0
                                    ? getProductTotalQuantityByUser(
                                        row.productId
                                      )
                                    : undefined;
                                const productContext =
                                  row.limit > 0
                                    ? {
                                        quantity: row.limit,
                                        totalQuantity: row.totalQuantity,
                                      }
                                    : undefined;
                                longPressTimerRef.current = setTimeout(() => {
                                  longPressTimerRef.current = null;
                                  longPressFiredRef.current = true;
                                  const canIncrease = handleIncreaseBy(
                                    row.productKey,
                                    10,
                                    productContext,
                                    ourSaved
                                  );
                                  if (!canIncrease) {
                                    toast.error(
                                      '該商品已達數量上限，無法再增加'
                                    );
                                    clearLongPressTimer();
                                    return;
                                  }
                                  longPressRepeatRef.current = setInterval(
                                    () => {
                                      const canIncreaseRepeat =
                                        handleIncreaseBy(
                                          row.productKey,
                                          10,
                                          productContext,
                                          ourSaved
                                        );
                                      if (!canIncreaseRepeat) {
                                        clearLongPressTimer();
                                        toast.error(
                                          '該商品已達數量上限，無法再增加'
                                        );
                                      }
                                    },
                                    LONG_PRESS_REPEAT_MS
                                  );
                                }, LONG_PRESS_MS);
                              }}
                              onPointerUp={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                clearLongPressTimer();
                                if (
                                  !longPressFiredRef.current &&
                                  !longPressCancelledRef.current
                                ) {
                                  if (
                                    row.limit > 0 &&
                                    row.totalQuantity >= row.limit
                                  ) {
                                    toast.error(
                                      '該商品已達數量上限，無法再增加'
                                    );
                                    return;
                                  }
                                  const productContext =
                                    row.limit > 0
                                      ? {
                                          quantity: row.limit,
                                          totalQuantity: row.totalQuantity,
                                        }
                                      : undefined;
                                  const ourSaved =
                                    row.limit > 0
                                      ? getProductTotalQuantityByUser(
                                          row.productId
                                        )
                                      : undefined;
                                  const canIncrease = handleIncreaseBy(
                                    row.productKey,
                                    1,
                                    productContext,
                                    ourSaved
                                  );
                                  if (!canIncrease) {
                                    toast.error(
                                      '該商品已達數量上限，無法再增加'
                                    );
                                  }
                                }
                                longPressFiredRef.current = false;
                                longPressCancelledRef.current = false;
                              }}
                              onPointerLeave={() => {
                                clearLongPressTimer();
                                longPressCancelledRef.current = true;
                              }}
                              onPointerCancel={() => {
                                clearLongPressTimer();
                                longPressCancelledRef.current = true;
                              }}
                            >
                              +
                            </Button>
                          </div>
                          {/* 小計 */}
                          <span className="text-lg font-semibold text-blue-600">
                            NT${selectedQty * row.price}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </CardContent>
            </Card>

            {/* 備註 */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">備註</CardTitle>
                <CardDescription>選填，交付時主辦可看到</CardDescription>
              </CardHeader>
              <CardContent>
                <textarea
                  value={remarks}
                  onChange={e => setRemarks(e.target.value)}
                  placeholder="例如：不要洋蔥、晚點到..."
                  className="w-full min-h-[80px] rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  maxLength={500}
                />
              </CardContent>
            </Card>

            {/* 總計 */}
            {myTotalQuantity > 0 && (
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6 space-y-3">
                  {/* 列出選擇的商品（含多規格） */}
                  {selectableRows.map(row => {
                    const qty = getItemQuantity(row.productKey);
                    if (qty <= 0) return null;
                    return (
                      <div
                        key={row.productKey}
                        className="flex justify-between items-center text-sm"
                      >
                        <span className="text-blue-700">{row.name}</span>
                        <span className="text-blue-900 font-medium">{qty}</span>
                      </div>
                    );
                  })}
                  <div className="flex justify-between items-center text-lg font-bold pt-2 border-t border-blue-300">
                    <span className="text-blue-900">總計：</span>
                    <span className="text-blue-900">NT${myTotalAmount}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 送出按鈕：有商品變更、暱稱或備註變更時顯示 */}
            {(hasPendingChanges ||
              remarks !== (activity?.remarks ?? '') ||
              (displayName ?? '').trim() !==
                (activity?.displayName ?? '').trim()) && (
              <Button
                className="w-full"
                size="lg"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? '送出中...' : '送出'}
              </Button>
            )}
            {/* 分享按鈕：送出成功後顯示，文案依商家回覆設定（prefix/變更內容/suffix、活動連結等） */}
            {shareTextAfterSubmit && !hasPendingChanges && (
              <Button
                className="w-full"
                size="lg"
                variant="outline"
                onClick={() => shareToLineOrClipboard(shareTextAfterSubmit)}
              >
                <Share2 className="w-4 h-4 mr-2" aria-hidden />
                分享
              </Button>
            )}
          </>
        )}
      </div>

      {/* 送出成功後跳出分享 Modal：僅分享到 LINE，內容依商家回覆設定 */}
      <Dialog open={shareModalOpen} onOpenChange={setShareModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>已送出</DialogTitle>
            <DialogDescription>
              內容依商家回覆設定，可分享至 LINE
            </DialogDescription>
          </DialogHeader>
          {shareTextAfterSubmit && (
            <pre className="text-xs text-muted-foreground bg-muted/50 rounded-md p-3 max-h-32 overflow-auto whitespace-pre-wrap break-words">
              {shareTextAfterSubmit}
            </pre>
          )}
          <DialogFooter>
            <Button
              className="w-full"
              onClick={() => {
                if (shareTextAfterSubmit) {
                  shareToLine(shareTextAfterSubmit);
                  setShareModalOpen(false);
                }
              }}
            >
              <Share2 className="w-4 h-4 mr-2" aria-hidden />
              分享到 LINE
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
